<!--
  Tracked against bank — the two books beside each other, and the gap between
  them. Never their sum.

  This is the section the owner asked for by name: "a section comparing money
  in and money out from manual tracking to bank statement tracking (should
  flag outstanding payments as well)". Outstanding is Still to collect, and in
  job scope it is the prominent figure on the section, because it is the one
  number that answers "have I been paid for this job".

  Two rules hold this section together, both of them about not lying:

  - The two books are never added. Every figure below belongs to exactly one
    of them and says which in its column heading. The only arithmetic that
    crosses the two is a SUBTRACTION — the two gaps — and it is done in
    src/lib/calc/compare.ts, which will not compute a gap at all when one side
    is missing. Adding a costed pour to the card payment that later paid for
    it is the double count this whole screen exists to remove.

  - A job with no bank lines shows dashes, not zeros. Nothing tagged is not
    the same as nothing spent, and "$0.00" under Cleared reads as a fact about
    the job that is not true. `money(null)` prints an em dash for exactly this.

  It is ALL TIME, deliberately, and the subtitle says so in words: money
  reaches the bank weeks after the concrete is poured, so a job read inside
  one month looks unpaid every time. The month picker at the top of the screen
  does not reach this section, and a reader must be able to see that without
  being told.

  Banked margin is expected to look alarming on any job still awaiting
  payment. It is labelled where it appears and Still to collect is kept beside
  it, so it is never read on its own.
-->
<script lang="ts">
  import { money, percent } from '../../../lib/money';
  import type { JobComparison } from '../../../lib/types';

  interface Props {
    /** The scope's own two books — one job's, or the whole company's. */
    comparison: JobComparison;
    /**
     * One row per job, all-time, with the Untagged bank lines last. Company
     * scope only; empty when the picker has narrowed to a single job.
     */
    register: readonly JobComparison[];
    /**
     * The period these figures cover, read off the RESPONSE's own scope — not
     * asserted here. This section asks for a whole-life scope, but what it
     * prints has to be what came back: the server signals that scope with the
     * exact shape `{ to_date: true }`, and if a response ever arrives without
     * it these figures are not all-time and this heading must not say they
     * are.
     */
    period: string;
    /** Company (all), or the one job the picker has narrowed to. */
    scope: string;
    isJobScope: boolean;
    /** Picks a job in the scope bar at the top of the screen. */
    onpick: (siteId: number) => void;
    /** Asks the screen to fetch both books again. */
    onretry: () => void;
    loading: boolean;
    failed: boolean;
  }

  const { comparison, register, period, scope, isJobScope, onpick, onretry, loading, failed }: Props =
    $props();

  const tracked = $derived(comparison.tracked);
  const banked = $derived(comparison.banked);

  /**
   * Net as a share of what actually came in. Its own sum of the BANKED side
   * only — it never reaches across to the tracked figures, which is why it is
   * written here rather than borrowed from `tracked`. Null, not 0%, when
   * nothing has come in.
   */
  const bankedMarginPct = $derived(
    banked === null || banked.money_in === 0 ? null : (banked.net / banked.money_in) * 100
  );

  /** Waiting on money is the normal reason a cleared margin looks like a disaster. */
  const awaitingMoney = $derived(comparison.still_to_collect !== null && comparison.still_to_collect > 0);

  /*
   * Both gaps are signed, and a signed figure under a fixed label can print a
   * claim that is not true.
   *
   * "Still to collect" is the owner's own word for the figure he asked for,
   * and it survives wherever it is true — the positive branch, which is the
   * one he asked about. It is not a licence to print the opposite under it:
   * "Still to collect −$54,134.33" has no correct reading, and the one a
   * contractor is likeliest to reach for is that he OWES it. When more has
   * cleared than was ever costed, the honest thing is a different label, and
   * the figure beneath it is printed UNSIGNED — the label already carries the
   * direction, and a minus as well would say it twice.
   *
   * `beyond_costed` is the same defect in the same block and gets the same
   * treatment, so the two cards cannot end up one honest and one not.
   */
  interface Gap {
    label: string;
    /** Unsigned — the label says which way round it is. */
    figure: number | null;
    /** Bad news, the accent, or neither. Read off the sign, like the label. */
    tone: 'owed' | 'bad' | 'plain';
  }

  const collect = $derived.by((): Gap => {
    const value = comparison.still_to_collect;
    if (value === null) return { label: 'Still to collect', figure: null, tone: 'plain' };
    if (value < 0) {
      return { label: 'Collected beyond what we costed', figure: -value, tone: 'plain' };
    }
    // Money owed is not bad news, it is the thing to chase — so it wears the
    // accent the register's outstanding column wears, not the loss red.
    return { label: 'Still to collect', figure: value, tone: value > 0 ? 'owed' : 'plain' };
  });

  const beyond = $derived.by((): Gap => {
    const value = comparison.beyond_costed;
    if (value === null) return { label: 'Beyond what we costed', figure: null, tone: 'plain' };
    if (value < 0) return { label: 'Costed but not yet paid out', figure: -value, tone: 'plain' };
    // Money that left the bank and never reached a Daily Report IS bad news.
    return { label: 'Beyond what we costed', figure: value, tone: value > 0 ? 'bad' : 'plain' };
  });
</script>

<section class="a-panel" aria-labelledby="compare-title">
  <div class="a-panel__head">
    <h2 class="a-panel__title" id="compare-title">The two books, side by side</h2>
    <p class="a-panel__sub">Tracked against bank · {period} · {scope} · ex-GST</p>
  </div>

  <p class="a-panel__note">
    All time, whatever the month picker above says: money reaches the bank weeks after the concrete
    does, so a job read inside one month looks unpaid every time. Nothing here adds the two columns
    together — the same load would be counted twice.
  </p>

  {#if failed}
    <!--
      A button, because this section's fetch is its own and the bank half's
      Try again is inside a block that is not rendered when the bank half
      succeeded. A section that can fail on its own needs its own way back.
    -->
    <p class="meta">The all-time figures did not come back. Nothing is wrong with them — the request failed.</p>
    <p>
      <button type="button" class="a-cta" onclick={onretry} disabled={loading}>
        {loading ? 'Trying…' : 'Try again'}
      </button>
    </p>
  {:else if loading && tracked === null && banked === null}
    <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading both books…</p>
  {:else}
    <div class="tablewrap">
      <table class="books">
        <caption class="visually-hidden">
          {scope} — {period}. What the Daily Reports costed, beside what cleared the bank
        </caption>
        <thead>
          <tr>
            <th scope="col"><span class="visually-hidden">Figure</span></th>
            <th scope="col" class="num">
              What we costed
              <small>Daily Reports, at your rates</small>
            </th>
            <th scope="col" class="num">
              What cleared the bank
              <small>Imported statements</small>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Money in</th>
            <td class="num a-num">{money(tracked?.revenue)}</td>
            <td class="num a-num">{money(banked?.money_in)}</td>
          </tr>
          <tr>
            <th scope="row">Money out</th>
            <td class="num a-num">{money(tracked?.cost)}</td>
            <td class="num a-num">{money(banked?.money_out)}</td>
          </tr>
          <tr>
            <th scope="row">Net</th>
            <td class="num a-num" class:is-bad={(tracked?.margin ?? 0) < 0}>{money(tracked?.margin)}</td>
            <td class="num a-num" class:is-bad={(banked?.net ?? 0) < 0}>{money(banked?.net)}</td>
          </tr>
          <tr>
            <th scope="row">Margin</th>
            <td class="num a-num" class:is-bad={(tracked?.margin_pct ?? 0) < 0}>{percent(tracked?.margin_pct)}</td>
            <td class="num a-num" class:is-bad={(bankedMarginPct ?? 0) < 0}>
              {percent(bankedMarginPct)}
              <small>on money that has cleared so far</small>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    {#if awaitingMoney}
      <!--
        The one caution this section must carry. A job poured, costed and not
        yet paid for shows a cleared margin deep in the red, and that figure is
        correct — it is only the wrong thing to read on its own.
      -->
      <p class="flag">
        <span class="pill pill--warn">
          Cleared margin is on money received so far. {money(comparison.still_to_collect)} of what was costed
          has not reached the bank yet.
        </span>
      </p>
    {/if}

    <!--
      What each column is built from, and it sits HERE rather than under the
      register, because the gaps beneath it are the figures the caveat changes
      and a reader must meet it before he reads them. The costed side is built
      job by job; a Daily Report filed against a typed-in project name has no
      job to sit under and is in neither column, while any bank line that paid
      for it still counts on the cleared side. That difference lands in the
      gaps below with nothing else naming it.
    -->
    {#if !isJobScope}
      <p class="a-panel__note">
        The two columns are company-wide: every job's Daily Reports on the costed side, every imported
        bank line on the cleared side — the Untagged ones included. Daily Reports that name no job site
        are in neither column, so part of the difference below is that, rather than work nobody priced.
      </p>
    {/if}

    <!-- ---- the two gaps ------------------------------------------------ -->
    <div class="gaps" class:gaps--lead={isJobScope}>
      <div class="gap gap--lead">
        <span class="gap__label">{collect.label}</span>
        <strong
          class="gap__fig a-num"
          class:is-bad={collect.tone === 'bad'}
          class:is-owed={collect.tone === 'owed'}>{money(collect.figure)}</strong
        >
        <span class="gap__say">
          {#if comparison.still_to_collect === null}
            Nothing to compare: one of the two books has no figures for {isJobScope
              ? 'this job'
              : 'the company'} yet.
          {:else if comparison.still_to_collect > 0}
            {money(comparison.still_to_collect)} poured and costed, not yet in the bank.
          {:else if comparison.still_to_collect < 0}
            <!--
              The third candidate is COMPANY-ONLY, and gated exactly like the
              population note above. In job scope the costed side is that job's
              own site row, which already holds every report tagged to it — so
              no report belonging to this job can be out of the costed column's
              reach, and offering it as an explanation would be handing the
              reader a cause that cannot be true. That is the category of copy
              this whole screen exists to remove.
            -->
            {money(-comparison.still_to_collect)} more has reached the bank than the Daily Reports account
            for — {#if isJobScope}money ahead of the work, or work no report has priced yet{:else}money
              ahead of the work, work no report has priced, or reports the costed column does not
              reach{/if}.
          {:else}
            Everything costed has reached the bank.
          {/if}
        </span>
      </div>

      <div class="gap">
        <span class="gap__label">{beyond.label}</span>
        <strong
          class="gap__fig a-num"
          class:is-bad={beyond.tone === 'bad'}
          class:is-owed={beyond.tone === 'owed'}>{money(beyond.figure)}</strong
        >
        <span class="gap__say">
          {#if comparison.beyond_costed === null}
            Nothing to compare: one of the two books has no figures yet.
          {:else if comparison.beyond_costed > 0}
            <!-- Same gate, same reason, as the card beside this one. -->
            {money(comparison.beyond_costed)} more has left the bank than the Daily Reports account for —
            real costs that never reached a Daily Report{#if !isJobScope}, or reports the costed column
              does not reach{/if}.
          {:else if comparison.beyond_costed < 0}
            {money(-comparison.beyond_costed)} of what was costed has not left the bank yet.
          {:else}
            What left the bank is exactly what was costed.
          {/if}
        </span>
      </div>
    </div>

    {#if !isJobScope && register.length > 0}
      <!-- ---- the jobs register ----------------------------------------- -->
      <h3 class="sub">Every job, both books</h3>
      <p class="a-panel__note">
        Pick a job's name to narrow the whole screen to it. A dash is a book with nothing in it for that
        job — not a zero, which would read as "spent nothing". A negative in the last column is the other
        direction: more has cleared for that job than was ever costed against it.
      </p>
      <div class="tablewrap">
        <table class="register">
          <caption class="visually-hidden">
            Every job — {period}. Costed figures beside cleared figures
          </caption>
          <thead>
            <tr>
              <th scope="col" rowspan="2">Job</th>
              <th scope="colgroup" colspan="4" class="num group">What we costed</th>
              <th scope="colgroup" colspan="2" class="num group">What cleared the bank</th>
              <th scope="col" rowspan="2" class="num">Still to collect</th>
            </tr>
            <tr>
              <th scope="col" class="num">Money in</th>
              <th scope="col" class="num">Money out</th>
              <th scope="col" class="num">Profit</th>
              <th scope="col" class="num">Margin</th>
              <th scope="col" class="num">Money in</th>
              <th scope="col" class="num">Money out</th>
            </tr>
          </thead>
          <tbody>
            {#each register as row (row.site_id ?? 'untagged')}
              <tr class:is-untagged={row.site_id === null}>
                <th scope="row" class="wrap">
                  {#if row.site_id === null}
                    {row.name}
                    <small>bank lines carrying no job</small>
                  {:else}
                    <button type="button" class="pickjob" onclick={() => onpick(row.site_id as number)}>
                      {row.name}
                    </button>
                  {/if}
                </th>
                <td class="num a-num">{money(row.tracked?.revenue)}</td>
                <td class="num a-num">{money(row.tracked?.cost)}</td>
                <td class="num a-num" class:is-bad={(row.tracked?.margin ?? 0) < 0}>{money(row.tracked?.margin)}</td>
                <td class="num a-num" class:is-bad={(row.tracked?.margin_pct ?? 0) < 0}>
                  {percent(row.tracked?.margin_pct)}
                </td>
                <td class="num a-num">{money(row.banked?.money_in)}</td>
                <td class="num a-num">{money(row.banked?.money_out)}</td>
                <td class="num a-num" class:is-owed={(row.still_to_collect ?? 0) > 0}>
                  {money(row.still_to_collect)}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}
</section>

<style>
  .books th[scope='col'],
  .register th {
    vertical-align: bottom;
  }
  .books small,
  .register small {
    display: block;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.72rem;
    color: var(--ink-faint);
  }
  .books th[scope='row'] { white-space: nowrap; }
  /* Neither book column is the primary one, so they are sized the same. */
  .books td { font-size: 1.02rem; font-weight: 700; }

  .group { border-bottom: 2px solid var(--line-soft); }

  /* A loss is a figure, not a fault — but it must not read as a win. */
  .is-bad { color: var(--hold); }
  /* Money owed is the figure he came here for; it carries the accent. */
  .is-owed { color: var(--accent-ink); font-weight: 800; }

  .is-untagged th,
  .is-untagged td { color: var(--ink-soft); }

  .flag { margin: 0; }

  .gaps {
    display: grid;
    gap: 0.6rem;
  }
  @media (min-width: 34rem) {
    .gaps { grid-template-columns: 1fr 1fr; }
    /* In job scope the outstanding figure answers the question the owner
       opened the screen with, so it is given the room to say so. */
    .gaps--lead { grid-template-columns: 3fr 2fr; }
  }
  .gap {
    display: grid;
    gap: 0.15rem;
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius);
    background: var(--bg-sunken);
  }
  .gaps--lead .gap--lead {
    background: var(--warn-tint);
    border-left: 4px solid var(--accent);
  }
  .gap__label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }
  .gap__fig {
    font-size: 1.25rem;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  .gaps--lead .gap--lead .gap__fig { font-size: 1.8rem; }
  .gap__say {
    font-size: 0.84rem;
    color: var(--ink-soft);
  }

  .sub {
    margin: 0.2rem 0 0;
    font-size: 0.95rem;
  }

  /* A job name that selects the job. A button rather than a clickable row:
     a clickable row is unreachable by keyboard and announces as nothing. */
  .pickjob {
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-weight: 700;
    color: var(--brand);
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    touch-action: manipulation;
  }
  .pickjob:hover { color: var(--brand-dark); }
  .pickjob:focus-visible {
    outline: 3px solid var(--brand);
    outline-offset: 2px;
  }

  .wrap { white-space: normal; min-width: 8rem; }
</style>
